// ============================================================
// TERROR — FACTION HALL
// 20×14, same footprint as Willie's Workshop.
// Left:   W'aelly   (Swords of Nullus)   [was W'yll]
// Right:  W'aolly   (Blades of Omnis)    [was W'illiam]
// Center: The Monolith (J'ynxi's Jesters)
//
// ⚠⚠ THE NAMES CHANGED 2026-08-20 AND THE JOKE BELOW DID NOT SURVIVE
// INTACT. caP'n, round 5, verbatim and unelaborated:
//
//     *"Omnis rep = W'aolly.  Nullus rep = W'aelly."*
//
// That is a direct ruling and it is the newest word, so it is applied:
// W'illiam is W'aolly and W'yll is W'aelly. But it OVERWRITES the
// naming reading written into this file on 2026-08-19 -- Will with the
// letters taken away for Nullus, William with the letters piled on for
// Omnis -- and the new pair does not carry that subtraction/addition
// shape. What it DOES carry is a different and arguably better one:
// each Willie now wears a syllable of the house he recruits for rather
// than of himself.
//
// ⚠ V'AEL IS FLAGGING THIS RATHER THAN QUIETLY RECONCILING IT, per the
// standing law that canon-vs-code disagreements go to caP'n. Every
// line below still gestures at one man on both sides of the room; if
// the 2026-08-19 reading is the one he wants, two strings come back.
//
// ⭐ THE TWO RECRUITERS ARE TWO WILLIES. caP'n, 2026-08-19:
//   "it's supposed to be 2 different willies -- this man's so powerful
//    he's got his avatars joining other Lords and recruiting FOR them
//    xD ... the player should put the pieces together themselves, we
//    will never outright say it's one single Willie at the Desk in the
//    Plaza and the rest are avatars."
//
// So the pieces are IN THE NAMES and nowhere else. The cast's naming
// law is that an apostrophe name is the joke said out loud once
// (X'aol = Exile, V'ael = Veil, Z'hefir = Zephyr). Applied here:
//   W'yll     = Will,    with the letters TAKEN AWAY  -> Nullus
//   W'illiam  = William, with the letters PILED ON    -> Omnis
// One man, subtracting from himself on the left of the room and
// accumulating on the right of it, recruiting against himself all day
// forever. NOTHING in this file may ever say that. The lines below
// gesture and never land; the player who has also met Willie, Willee
// and W'ily is the one who gets it.
// ════════════════════════════════════════════════════════════
// ⭐ WHY THIS ROOM EXISTS AT ALL — caP'n, 2026-08-20, verbatim
// ════════════════════════════════════════════════════════════
//   *"The Faction Hall exists as a thank you to only Omnis and Nullus
//    (even though they despise each other). V'ael knew them growing up
//    and still appreciates both, so she has allowed representatives to
//    recruit Aenarchists in her own Emperium -- very unheard of, only a
//    few Lords are known to be this lax with their Soul sharing, as
//    Souls are Divine Crests waiting to happen."*
//
// Three things fall out of that and they are all load-bearing:
//
//   1 · THE HALL IS A FAVOUR, NOT AN INSTITUTION. It stands in the
//       Plaza -- V'ael's own Emperium, because her Divine Jewel is in
//       it -- and she is letting two rival powers fish in her water.
//       Nobody in this room is here by right.
//   2 · IT IS A THANK YOU AND THE DEBT IS NEVER NAMED. She knew them
//       growing up. That is all the player gets and all they should
//       get; the reps do not know what it was either.
//   3 · ⭐ AND IT IS EXPENSIVE. A Soul is a Divine Crest waiting to
//       happen, so every Aenarchist recruited out of this hall is
//       currency walking out of V'ael's door. That is the measure of
//       the favour, it is the reason "only a few Lords are this lax,"
//       and it is why the reps are POLITE. They are guests.
//
// ⚠ NONE OF THIS MAY BE SAID OUTRIGHT IN THIS FILE. Same law as the
// two Willies below: the room gestures, the player assembles. The
// creeds are what the WORLD believes about each faction; the reps
// speak as people who know they are standing in someone else's house.
//
// Engine: PlayerEntity, MobileDpad, RoomConsole (The Treatment, C5)
// Movement: full 4-directional (upgraded from left/right only)
// ============================================================


// ── Grimoire Manuscript Palette ──────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from "react";
import { SD } from "./theme";
import { ref, get, update } from "firebase/database";
import { db } from "./firebase";
import { getAllGrimoireCards } from "./cards";
import {
  CDN, cardUrl,
  PlayerEntity,
  MobileDpad, usePlayerAnimation } from "./engineIndex";
import { useEquippedCompanion } from "./useEquippedCompanion";
import RoomConsole, { getSessionLog, setSessionLog } from "./RoomConsole";
import HoloFoil, { holoGlowStyle } from "./HoloFoil";
import { addCardToCollection, grantCrests } from "./progression";

const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  ruling:        "rgba(139,106,48,0.18)",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

const MANUSCRIPT_BG = [
  "repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(139,106,48,0.05) 27px, rgba(139,106,48,0.05) 28px)",
  "radial-gradient(ellipse at 25% 15%, rgba(120,80,20,0.18) 0%, transparent 55%)",
  "radial-gradient(ellipse at 75% 85%, rgba(100,60,15,0.15) 0%, transparent 55%)",
  "#0f0c07",
].join(", ");


// ─────────────────────────────────────────────────────────────
// ZONE CONSTANTS
// ─────────────────────────────────────────────────────────────
const COLS  = 20;
const ROWS  = 14;
const SPAWN = { x: 9, y: 12 };
const T     = { FLOOR: 0, WALL: 1, BLOCKED: 2 };
// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const isMobile = () => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
};

function calcTile() {
  return Math.min(
    Math.floor(window.innerWidth  / COLS),
    Math.floor(window.innerHeight / ROWS)
  );
}

// ─────────────────────────────────────────────────────────────
// FACTIONS
// ─────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
// ⭐ THE CREEDS — what the WORLD believes, 2026-08-20
// ═══════════════════════════════════════════════════════════════
// caP'n, round 5, verbatim on all three:
//
//   *"Nullus is perceived as the Lyt of the world — white, good,
//    Stopping of Evil."*
//   *"Omnis is perceived as the Lyf of the world — black, bad,
//    Destroying of Evil (furthering of it)."*
//   *"Jesters: they want the game to continue as long as possible
//    unless it becomes stagnant and everyone claws at it — they will
//    without hesitation flip the board and start a new game. For the
//    player: Jesters promise to help you whether you're good or evil,
//    they just want you to be free."*
//
// ⭐ "PERCEIVED AS" IS THE WHOLE SENTENCE AND IT IS NOT A HEDGE. Terror
// has never had a good side. Nullus STOPS evil and Omnis DESTROYS it,
// and destroying evil is how you further it — that is the joke and the
// player is meant to arrive at it alone. So `creed` is written as
// REPUTATION, in the voice of a world that has decided which of them
// is the nice one, and nothing in this file ever confirms it.
//
// ⚠ AND THE PARENTHESIS IS THE POINT. "Destroying of Evil (furthering
// of it)" is caP'n's own aside and it never reaches the screen. Omnis
// says the first half out loud. The second half is what the player
// works out after watching W'aolly collect.
//
// ⚠ WHITE AND BLACK ARE THE PERCEPTION, NOT THE PALETTE. Nullus is
// gold in this room and Omnis is violet, and those are unchanged — the
// creed describes what people SAY about them, not what the Hall looks
// like. Repainting a room is a visual ruling and has not been asked
// for.
//
// The Jesters' `creed` is deliberately the only one that tells the
// truth, because the Jesters are the only faction with nothing to gain
// by lying about themselves.
export const FACTIONS = {
  nullus: {
    id: "nullus", name: "Swords of Nullus", short: "Nullus",
    color: "#d4a84b", rep: "W'aelly", repSubtitle: "The Hollow Knight",
    creed: "The Lyt of the world. White, and good, and the hand that STOPS evil.",
    creedSub: "That is what is said about them. They have never once corrected it.",
    holoTier: "holo_nihil",
    x: 3, y: 11,
    greeting: [
      "...", "You feel it, don't you.",
      "That hollow space where certainty used to live.",
      "That is where we begin.",
      "The Swords of Nullus do not fight for glory.",
      "We fight because everything else is just noise.",
      "Will you join the void, Aenarchist?",
    ],
    memberLines: [
      "The void holds.", "Your battles speak for us.",
      "Keep pushing into the Depths.",
      "Nullus remembers every Solrend you've won.",
      // ── the gesture. Never the landing. ──────────────────────
      // Each of these is a thing you could only know about a man if
      // you were him, delivered as if it were ordinary spite. None
      // of them says anything. Together they itch.
      "Don't take the long way round. He'll talk at you.",
      "He counts things. That is the entire man. I got the better half.",
      "We were the same height once. I stopped adding.",
    ],
    challenges: [
      // ⭐ `crests` IS THE ◆ PAYOUT AND IT IS AUTHORED HERE (2026-08-20).
      // Priced by THE THREE RANKS, which is the Empire's ladder and not
      // a local invention: a rung anyone will pass on the way through
      // pays a Champ's 3, and a rung labelled "Exclusive" -- the third
      // in every faction, and all three of the Jesters' -- pays a
      // Lord's 5. grantReward reads the field and never learns a
      // faction's name; an unpriced row silently pays nothing, which is
      // the correct failure for a content gap.
      //
      // NULLUS ASKS FOR THE DESCENT. Nothing here is a win count -- the
      // Swords do not care that you beat someone, they care that you kept
      // walking after the room went quiet. Every track below is a stat the
      // app ALREADY writes; no new write path exists for any of them.
      { id: "nullus_1", desc: "Descend to Depth 3",    goal: 3,  track: "bestStreak",  reward: "Curio: Circle of Lyf",       rewardId: "curio_circle_of_lyf", crests: 3 },
      { id: "nullus_2", desc: "Descend to Depth 8",    goal: 8,  track: "bestStreak",  reward: "Tome: Void",                 rewardId: "tome_lyt_footed", crests: 3 },
      { id: "nullus_3", desc: "Silence 3 Gatekeepers", goal: 3,  track: "gatekeepers", reward: "Exclusive: Seal of Nihil", rewardId: "tome_seal_of_nihil", crests: 5 },
    ],
  },
  omnis: {
    id: "omnis", name: "Blades of Omnis", short: "Omnis",
    color: "#7c3aed", rep: "W'aolly", repSubtitle: "The Collector",
    creed: "The Lyf of the world. Black, and bad, and the hand that DESTROYS evil.",
    creedSub: "They will tell you the difference between stopping a thing and destroying it. They will not tell you what destroying it requires.",
    holoTier: "holo_omnia",
    x: 15, y: 12,
    greeting: [
      "Oh — a new face.", "Wonderful. I do love new faces.",
      "I've catalogued every Aenarchist who's walked through here.",
      "Their decks, their habits, their tells.",
      "The Blades of Omnis collect everything.",
      "Knowledge. Power. Victory.",
      "You look like someone worth collecting. Join us?",
    ],
    memberLines: [
      "Everything belongs to us eventually.",
      "Your progress is noted. Appreciated.",
      "Omnis grows with every Solrend won.",
      "Keep going. We're watching.",
      // ── the same gesture from the other side of the room ─────
      "You've been over there. You've got the draught on you.",
      "He'll tell you he took something away. He didn't. He mislaid it.",
      "Nine thousand times I have heard that pitch. I could give it for him.",
    ],
    challenges: [
      // OMNIS ASKS FOR THE CATALOGUE. W'illiam collects, so the middle rung
      // counts what you HOLD rather than what you beat -- the one thing
      // no other faction asks about. Same rule as Nullus: every track is
      // a stat the app already writes.
      { id: "omnis_1", desc: "Win 3 Solrends",              goal: 3,  track: "totalWins", reward: "Curio: Mage's Amulet",     rewardId: "curio_mages_amulet", crests: 3 },
      { id: "omnis_2", desc: "Catalogue 75 distinct cards", goal: 75, track: "codex",     reward: "Tome: Gauntlets of A'zka", rewardId: "tome_gauntlets_azka", crests: 3 },
      { id: "omnis_3", desc: "Win 10 Solrends",             goal: 10, track: "totalWins", reward: "Exclusive: Seal of Omnis", rewardId: "tome_seal_of_omnis", crests: 5 },
    ],
  },
  jesters: {
    id: "jesters", name: "J'ynxi's Jesters", short: "Jesters",
    creed: "Neither. Whether you are good or evil, they will help you — they only want you free.",
    creedSub: "The pillar wants the game to go on as long as it possibly can. Right up until nobody is playing any more. Then it turns the board over and deals again.",
    // ⭐ THE SPEAKER IS THE MONOLITH, NOT HER. Two laws meet on this
    // one field and they agree.
    //
    // caP'n on J'ynxi: "she is INDIFFERENT — true Chaos, nature's
    // equaliser. Never malicious AND never kind. She reads as weather."
    // And on placing her: "never add J'ynxi, I will do that."
    //
    // This block had her named as the speaker and then written as a
    // warm patron who had picked the player out: "you're already one of
    // us, honestly" · "the others think they own you — they don't" ·
    // "keep playing YOUR way" · and, worst, "the Grimoire chose you,
    // you know. Not them. Not the void. Not everything. YOU." That is a
    // chosen-one, in a game whose whole first premise is that an
    // AENARCHIST IS UNLICENSED AND NOBODY PICKED THEM.
    //
    // The fix is not to make her colder. It is to notice she was never
    // supposed to be standing here. It is a PILLAR you pray at. So the
    // pillar speaks, the lines are things that would be equally true if
    // the player walked away, and whether anything is behind it is the
    // player's problem — which is exactly the shape caP'n keeps asking
    // for and costs nothing to hold.
    //
    // TEST FOR ANY NEW LINE HERE: could it be said to an empty room?
    // If it needs the player to be listening, it is the wrong line.
    color: "#22c55e", rep: "The Monolith", repSubtitle: "It does not answer",
    holoTier: "holo_jynxi",
    x: 9, y: 8, trigger: { x: 9, y: 11 }, isMonolith: true,
    greeting: [
      "...",
      "Most people walk past this.",
      "The other two are loud. That is not a criticism; it is how recruiting works.",
      "Nullus wants nothing. Omnis wants everything. Both are a lot of paperwork.",
      "There is no oath here. No rank. No roster.",
      "Things happen to people who stand near this pillar.",
      "Good things happen. Bad things happen. The pillar does not sort them.",
      "Stand there or don't.",
    ],
    memberLines: [
      "...",
      "The pillar is warm today. It is often warm. It means nothing.",
      "Your Solrends are counted. So is everyone's.",
      "Something was going to happen to you regardless.",
      "The weather is not on your side. The weather is not on anyone's side.",
    ],
    recognitionLines: {
      // ⚠ These are the OTHER two reps reacting to a player who stands
      // at the pillar — W'aelly on the left, W'aolly on the right. They
      // were defined, checked for, and then never shown; see handleSpeak.
      nullus: "...You have been standing at the pillar. That happens to people. Do not let it distract you from the void.",
      omnis:  "Oh, the pillar. I have catalogued everyone it has ever been warm for. You are in company. Peculiar company, but company.",
    },
    challenges: [
      { id: "jesters_1", desc: "Activate 5 Nihil tomes in one Solrend",           goal: 5, reward: "Exclusive: J'ynxi's Wink",     rewardId: "aff_jynxi",             hint: "Freely given to those who prayed seven times.", crests: 5 },
      { id: "jesters_2", desc: "Play both Nullus & Omnis tomes in 3 Solrends",     goal: 3, reward: "Exclusive: Grimoire Fragment", rewardId: "tome_devils_advocate",  hint: "Shown only to those who play both sides of the void.", crests: 5 },
      { id: "jesters_3", desc: "Win 5 Solrends using only Ancient Sorcery tomes",  goal: 5, reward: "Exclusive: Jester's Seal",    rewardId: "tome_soulcerers_seal",  hint: "Earned when ancient whispers carry the day.", crests: 5 },
    ],
    easterEgg: {
      requirements: [
        { id: "pray7",         desc: "Pray at the Monolith 7 times" },
        { id: "jynxi_win",     desc: "Win a Solrend using only J'ynxi Affinity" },
        { id: "depth10",       desc: "Reach Depth 10 in Endless Depths" },
        { id: "both_factions", desc: "Hold allegiance to both Nullus and Omnis" },
      ],
      // Same register as everything else at this pillar. The old set
      // ended on "the Grimoire chose you, you know. Not them. Not the
      // void. Not everything. You." — a chosen-one, awarded for doing
      // four difficult things, in the one faction whose entire point is
      // that nobody was chosen. What replaces it pays the player in
      // ACKNOWLEDGEMENT rather than in destiny, which is both truer and
      // rarer: the pillar notices, once, and does not make it mean
      // anything.
      secretLines: [
        "...",
        "Four unlikely things, in one order.",
        "That is not a sign. It is four unlikely things.",
        "The pillar has been counting since before the Plaza had a floor.",
        "It has counted this once.",
        "...",
        "Go on, then.",
      ],
    },
  },
};

// ─────────────────────────────────────────────────────────────
// MAP
// ─────────────────────────────────────────────────────────────
function buildMap() {
  const map = Array.from({ length: ROWS }, () => Array(COLS).fill(T.FLOOR));
  // Outer boundary
  for (let c = 0; c < COLS; c++) { map[0][c] = T.WALL; map[ROWS-1][c] = T.WALL; }
  for (let r = 0; r < ROWS; r++) { map[r][0] = T.WALL; map[r][COLS-1] = T.WALL; }
  // ── CEILING WALL (Apr 2026 fix) ───────────────────────────
  // The background art only has marble floor on rows 10–12. Everything
  // above that (rows 1–9) is decorative architecture — the ceiling, the
  // pillars, the banners above. Previously the player could walk straight
  // up into that art because only row 0 was walled. Wall off rows 1–9
  // across the entire width so the player stays on the marble floor.
  //
  // NOTE: preserve row 10 across the rep x-coords so the future "walk
  // behind the rep" easter egg (retired horizon memory) still works —
  // the player needs to be able to slip to row 10 near W'yll (x:3)
  // and W'illiam (x:15) to trigger the hidden wall whispers.
  for (let r = 1; r < 10; r++) {
    for (let c = 1; c < COLS - 1; c++) {
      map[r][c] = T.WALL;
    }
  }
  // Rep collision blocks (face of the NPC, player cannot stand ON them)
  for (let dx = -1; dx <= 1; dx++) {
    if (FACTIONS.nullus.x+dx >= 0)  map[FACTIONS.nullus.y][FACTIONS.nullus.x+dx] = T.BLOCKED;
    if (FACTIONS.omnis.x+dx < COLS) map[FACTIONS.omnis.y][FACTIONS.omnis.x+dx]  = T.BLOCKED;
  }
  // Monolith pillar — blocks center 2 cols from bottom through row 10.
  // Rows 2–9 are already WALL from the ceiling block above, so this
  // effectively just extends the pillar down through rows 10 (blocked
  // on the walkable floor too — you cannot walk through the Monolith).
  for (let r = 10; r < 11; r++) { map[r][9] = T.BLOCKED; map[r][10] = T.BLOCKED; }
  // Bottom row — solid wall
  for (let c = 1; c < COLS - 1; c++) map[ROWS-1][c] = T.WALL;
  return map;
}
const MAP = buildMap();

function isWalkable(x, y) {
  if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
  return MAP[y][x] === T.FLOOR;
}

function getNearbyFaction(x, y) {
  // THE MONOLITH IS PAINTED WHERE IT CANNOT BE STOOD ON. Rows 1-9 are
  // wall, so the stone sits at (9,8) and is spoken to from (9,11), the
  // floor in front of it. "Tidying" this to read f.x/f.y would kill the
  // entire Jesters faction -- hence the explicit `trigger` field.
  const mono = FACTIONS.jesters.trigger;
  if (Math.abs(x - mono.x) <= 2 && Math.abs(y - mono.y) <= 2) return FACTIONS.jesters;
  for (const f of Object.values(FACTIONS)) {
    if (f.isMonolith) continue;
    if (Math.abs(x - f.x) <= 3 && Math.abs(y - f.y) <= 3) return f;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// FACTION MENU — FactionHall-specific, stays here
// ─────────────────────────────────────────────────────────────
function FactionMenu({ faction, playerFaction, playerName, uid, profile, onJoin, onClose, isJester }) {
  const isMember  = faction.id === "jesters" ? isJester : playerFaction === faction.id;
  const otherSide = faction.id === "jesters" ? false : (playerFaction && playerFaction !== faction.id);
  const [progress, setProgress] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [claimed,  setClaimed]  = useState({});

  // ── JESTERS SPECIAL: Pray-7 auto-unlock tier ─────────────────
  // If faction is Jesters and player has prayed 7+ times, the menu appears
  // even if they haven't yet "joined" — claiming the first reward below
  // performs the official pledge (set isJester: true + write member row).
  const jestersUnlocked = faction.id === "jesters" && (
    isJester || (profile?.monolithPrayers || 0) >= 7
  );
  // "Visible" = permitted to see the menu content (member OR pray-7 Jester tier)
  const visibleAsPreview = faction.id === "jesters" && !isJester && jestersUnlocked;
  const canViewContent   = isMember || visibleAsPreview;

  // Card lookup for rewards display
  const allCards = getAllGrimoireCards();
  const getRewardCard = (rewardId) => allCards.find(c => c.id === rewardId);

  useEffect(() => {
    if (!uid || !canViewContent) return;
    // Only pull progress for actual members — preview-tier Jesters have no progress row yet
    if (!isMember) return;
    // REKEY: member rows are keyed by uid (matches Firebase rules + survives name changes)
    get(ref(db, `factions/${faction.id}/members/${uid}`)).then(snap => {
      if (snap.exists()) setProgress(snap.val());
    });
  }, [uid, faction.id, isMember, canViewContent]);

  // ── PROGRESS SOURCE ──────────────────────────────────────────
  // Generic challenges (track: "totalWins" | "bestStreak") read straight
  // from stats the app already keeps, so they're genuinely reachable with
  // NO new write path: totalWins is the lifetime Solrend win count, and in
  // Endless Depths a run's depth IS its win streak, so bestStreak is the
  // deepest depth ever reached. Challenges WITHOUT a track are in-match
  // predicates (e.g. "activate 5 Nihil tomes in one Solrend") and still
  // read the member row — they stay at 0 until that in-match instrumentation
  // is wired (see roadmap). The Jesters pray-7 first-boon path is unaffected.
  const statFor = (track) => {
    if (track === "totalWins")   return profile?.totalWins || 0;
    if (track === "bestStreak")  return profile?.endlessDepths?.bestStreak || 0;
    // Named bosses put down in the Depths. Written by the depth recorder
    // (`defeatedGatekeepers`). FIVE exist, so a goal above 5 is a
    // challenge nobody can ever finish.
    if (track === "gatekeepers") return (profile?.endlessDepths?.defeatedGatekeepers || []).length;
    // Distinct card ids held. `collection` is already on the profile
    // every caller hands us.
    if (track === "codex")       return Object.keys(profile?.collection || {}).length;
    return 0;
  };

  // Grant the actual reward card into the player's collection — the
  // canonical append-only, provenance-stamped transaction from progression.
  // These faction boons are earned in their faction's own foil, so they're
  // granted as a holo of the faction tier (holo_nihil / holo_omnia /
  // holo_jynxi). Append-only + delta transaction = nothing can be wiped.
  const grantReward = async (challenge) => {
    await addCardToCollection(
      uid,
      challenge.rewardId,
      true,                 // isHolo — faction boons arrive foiled
      faction.holoTier || null,
      `faction_${faction.id}`,
    );

    // ── ◆ THE CHALLENGES PAY THE RESERVE ─────────────────────
    // caP'n, 2026-08-20, naming the Crest faucets: *"easter eggs,
    // achievements, and challenges."* These are the challenges. Three
    // per faction, one-time each, claimed by hand — which is the exact
    // shape a ◆ grant is allowed to have.
    //
    // ⭐ THE NUMBER IS ON THE ROW, NOT HERE. `challenge.crests` is
    // authored in FACTIONS beside the goal it belongs to, so a
    // fourth challenge prices itself and this function never learns a
    // faction's name. Silently paying nothing for an unpriced row is
    // the correct failure: a missing number is a content gap, not a
    // reason to invent a payout.
    //
    // Non-fatal, and it runs AFTER the card: the card is the reward
    // the screen promised, and a currency hiccup must not cost it.
    if (challenge.crests > 0) {
      try { await grantCrests(uid, challenge.crests, `faction:${faction.id}:${challenge.id}`); }
      catch (e) { console.warn("[FactionHall] Crest pour failed:", e && e.message); }
    }
  };

  const handleClaim = async (challenge, isFirstPledge = false) => {
    if (claiming) return;
    // Never grant twice.
    if (progress?.[`${challenge.id}_claimed`] || claimed[challenge.id]) return;
    setClaiming(challenge.id);

    // JESTERS FIRST CLAIM — also performs the official pledge in one action
    if (isFirstPledge && faction.id === "jesters" && !isJester) {
      try {
        // Grant the card FIRST; only pledge + mark claimed if it succeeds,
        // so a failed grant never burns the claim.
        await grantReward(challenge);
        await update(ref(db, `profiles/${uid}`), { isJester: true });
        await update(ref(db, `factions/jesters/members/${uid}`), {
          joinedAt: Date.now(),
          rank: 0,
          name: playerName,
          [`${challenge.id}_claimed`]: true,
        });
      } catch (err) {
        console.error("[FactionHall] first-pledge claim failed:", err);
        setClaiming(null);
        return;
      }
      // Tell the parent component the player is now a Jester (updates badge etc.)
      if (onJoin) onJoin("jesters", { skipMenuReopen: true });
      setClaimed(p => ({ ...p, [challenge.id]: true }));
      setClaiming(null);
      return;
    }

    // Normal progress-gated claim
    const prog = challenge.track ? statFor(challenge.track) : (progress?.[challenge.id] || 0);
    if (prog < challenge.goal) { setClaiming(null); return; }
    try {
      // Grant BEFORE writing the claimed flag — if the grant throws, the
      // reward isn't marked claimed and the player can retry (nothing lost).
      await grantReward(challenge);
      await update(ref(db, `factions/${faction.id}/members/${uid}`), {
        [`${challenge.id}_claimed`]: true,
      });
    } catch (err) {
      console.error("[FactionHall] claim failed:", err);
      setClaiming(null);
      return;
    }
    setClaimed(p => ({ ...p, [challenge.id]: true }));
    setClaiming(null);
  };

  // ── Decorative corner flourishes ─────────────────────────────
  const cornerFlourish = (corner) => {
    const pos = {
      tl: { top: 8, left: 8, transform: "rotate(0deg)" },
      tr: { top: 8, right: 8, transform: "scaleX(-1)" },
      bl: { bottom: 8, left: 8, transform: "scaleY(-1)" },
      br: { bottom: 8, right: 8, transform: "scale(-1,-1)" },
    }[corner];
    return (
      <div style={{ position:"absolute", ...pos, width:28, height:28,
        pointerEvents:"none", opacity:0.5 }}>
        <svg viewBox="0 0 28 28" style={{ width:"100%", height:"100%" }}>
          <path d="M 2 2 L 2 14 M 2 2 L 14 2 M 2 2 Q 8 8 14 2 M 2 2 Q 8 8 2 14"
            stroke={faction.color} strokeWidth="1.2" fill="none" opacity="0.7" />
          <circle cx="2" cy="2" r="1.2" fill={faction.color} opacity="0.8" />
        </svg>
      </div>
    );
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:250,
      background:"radial-gradient(circle at center, rgba(10,8,4,0.88), rgba(4,3,1,0.97))",
      display:"flex", alignItems:"center", justifyContent:"center", fontFamily:GM.font,
      backdropFilter:"blur(3px)" }}
      onClick={onClose}>

      {/* Reward-slot breathing glow now comes from holoGlowStyle(holoTier)
         (per-faction color, keyframes injected into <head> once) — the old
         green-hardcoded inline <style> pulse was removed. */}

      <div onClick={e => e.stopPropagation()} style={{
        position:"relative",
        width:"min(94vw, 540px)", maxHeight:"88vh", overflowY:"auto",
        background:`
          linear-gradient(145deg, ${GM.pageWarm} 0%, ${GM.pageMid} 50%, ${GM.page} 100%),
          repeating-linear-gradient(0deg, transparent 0px, transparent 28px, ${GM.ruling} 28px, ${GM.ruling} 29px)
        `,
        backgroundBlendMode:"multiply",
        border:`1px solid ${faction.color}44`,
        borderRadius:6,
        padding:"0",
        boxShadow:`
          0 0 60px ${faction.color}33,
          0 0 120px ${faction.color}11,
          inset 0 0 80px rgba(0,0,0,0.55),
          inset 0 0 1px ${faction.color}88
        `,
      }}>

        {/* Corner flourishes */}
        {cornerFlourish("tl")}
        {cornerFlourish("tr")}
        {cornerFlourish("bl")}
        {cornerFlourish("br")}

        {/* Faction color aura bar at top */}
        <div style={{
          height:4,
          background:`linear-gradient(90deg, transparent, ${faction.color}, transparent)`,
          opacity:0.6,
        }} />

        {/* ── HEADER — faction sigil + name + title ─────────────── */}
        <div style={{ textAlign:"center", padding:"32px 32px 18px" }}>
          <div style={{
            fontSize:9, color:faction.color, letterSpacing:6, marginBottom:10,
            opacity:0.85,
          }}>
            ◆ {faction.name.toUpperCase()} ◆
          </div>

          {/* Illuminated drop cap rep name */}
          <div style={{
            fontSize:32, fontWeight:900, fontStyle:"italic",
            color:GM.vellum, fontFamily:GM.fontD || GM.font,
            textShadow:`0 0 20px ${faction.color}66, 0 2px 4px rgba(0,0,0,0.6)`,
            letterSpacing:2, marginBottom:2,
          }}>
            {faction.rep}
          </div>

          <div style={{
            fontSize:11, color:GM.vellumFaint, fontStyle:"italic",
            fontFamily:GM.font, letterSpacing:2, marginTop:4,
          }}>
            ― {faction.repSubtitle} ―
          </div>

          {/* ◆ THE CREED. See THE CREEDS above FACTIONS: this is what
              the WORLD says about this house, not what the house is.
              It sits under the rep because the rep is the house's face
              and a reputation is a thing you meet before a person. */}
          {faction.creed && (
            <div style={{ marginTop:16, padding:"0 6px" }}>
              <div style={{
                fontSize:11.5, color:faction.color, fontFamily:GM.font,
                lineHeight:1.75, fontStyle:"italic",
                textShadow:`0 0 10px ${faction.color}33`,
              }}>
                {faction.creed}
              </div>
              {faction.creedSub && (
                <div style={{
                  marginTop:7, fontSize:10, color:GM.vellumFaint,
                  fontFamily:GM.font, lineHeight:1.7, fontStyle:"italic", opacity:.9,
                }}>
                  {faction.creedSub}
                </div>
              )}
            </div>
          )}

          {/* Hairline divider */}
          <div style={{
            marginTop:18,
            height:1,
            background:`linear-gradient(90deg, transparent, ${GM.inkBrown}88, transparent)`,
          }} />
        </div>

        {/* ── NON-MEMBER: Pledge button (Nullus/Omnis) ──────────── */}
        {!isMember && !otherSide && !visibleAsPreview && (
          <div style={{ textAlign:"center", padding:"8px 32px 32px" }}>
            <div style={{
              fontSize:12, color:GM.vellumDim, fontStyle:"italic",
              marginBottom:22, lineHeight:1.9, fontFamily:GM.font,
            }}>
              Pledge your blade to {faction.name}.<br/>
              <span style={{ color:GM.vellumFaint, fontSize:10 }}>
                Unique challenges. Exclusive rewards. A faction rank worn proudly.
              </span>
            </div>
            <button onClick={() => onJoin(faction.id)} style={{
              padding:"14px 40px",
              background:`linear-gradient(135deg, ${faction.color}33, ${GM.pageMid} 50%, ${faction.color}22)`,
              border:`1px solid ${faction.color}`,
              borderRadius:3, cursor:"pointer",
              color:faction.color, fontSize:15, fontWeight:900, fontStyle:"italic",
              fontFamily:GM.font, letterSpacing:3,
              boxShadow:`0 0 24px ${faction.color}55, inset 0 0 12px ${faction.color}22`,
              textShadow:`0 0 8px ${faction.color}`,
            }}>◆ PLEDGE ALLEGIANCE ◆</button>
          </div>
        )}

        {/* ── ALREADY OTHER FACTION ─────────────────────────────── */}
        {otherSide && (
          <div style={{ textAlign:"center", padding:"8px 32px 32px" }}>
            <div style={{
              fontSize:12, color:GM.vellumFaint, fontStyle:"italic",
              lineHeight:1.9, fontFamily:GM.font,
            }}>
              You've already pledged to another faction.<br/>
              <span style={{ color:GM.vellumFaint, fontSize:10 }}>
                Switching is possible — but you'll lose your current rank.
              </span>
            </div>
            <button onClick={() => onJoin(faction.id)} style={{
              marginTop:22, padding:"10px 26px", background:"transparent",
              border:`1px solid ${GM.inkBrown}66`, borderRadius:2, cursor:"pointer",
              color:GM.vellumFaint, fontSize:11, fontFamily:GM.font, letterSpacing:1,
            }}>Switch Allegiance (lose progress)</button>
          </div>
        )}

        {/* ── MEMBER CONTENT (or Jesters Preview after Pray-7) ──── */}
        {canViewContent && (
          <div style={{ padding:"8px 28px 28px" }}>

            {/* Preview banner — only for pray-7-unlocked non-joined Jester tier */}
            {visibleAsPreview && (
              <div style={{
                marginBottom:18, padding:"14px 16px",
                background:`linear-gradient(135deg, ${faction.color}22, transparent)`,
                border:`1px dashed ${faction.color}88`,
                borderRadius:4, textAlign:"center",
              }}>
                <div style={{ fontSize:10, color:faction.color, letterSpacing:3, marginBottom:6, fontWeight:900 }}>
                  ◆ THE MONOLITH STIRRED ◆
                </div>
                <div style={{ fontSize:11, color:GM.vellumDim, fontStyle:"italic", lineHeight:1.7 }}>
                  Seven prayers were noticed. Claim your first boon below —<br/>
                  and by taking it, you pledge to J'ynxi's unchosen court.
                </div>
              </div>
            )}

            <div style={{
              fontSize:10, color:faction.color, letterSpacing:4, marginBottom:16,
              textAlign:"center", fontWeight:900,
            }}>
              ◆ FACTION REWARDS ◆
            </div>

            {faction.challenges.map((ch, idx) => {
              const prog = ch.track ? statFor(ch.track) : (progress?.[ch.id] || 0);
              const alreadyClaimed = progress?.[`${ch.id}_claimed`] || claimed[ch.id];
              const complete = prog >= ch.goal;
              const rewardCard = getRewardCard(ch.rewardId);
              const rewardName = rewardCard?.name || ch.reward;
              const rewardImg  = rewardCard?.id ? cardUrl(rewardCard.id) : null; // CDN law — one builder (the Khao alias lives there)

              // Preview-tier Jester: first reward is claimable on sight, rest locked
              const isFirstPledge = visibleAsPreview && idx === 0;
              // Hide unlock hint: show reward, hide HOW to get it
              // For preview-tier: first row shows "Claim to Pledge", others show "???"
              // For members: completed rows show progress, incomplete rows show "???"
              const showUnlockHint = alreadyClaimed || complete || isFirstPledge;

              // ── PER-FACTION HOLO ─────────────────────────────────
              // Every faction's reward slots now wear their OWN foil, keyed
              // off the faction's holoTier: Nihil → holo_nihil (white pillars,
              // black diamonds cascading), Omnia → holo_omnia (black/white
              // bullseyes surfacing), J'ynxi → holo_jynxi (the grinning green).
              // One shared <HoloFoil> tier = the SAME foil the shop/pull show.
              const holoTier = faction.holoTier;
              const hasHolo  = !!holoTier;

              return (
                <div key={ch.id} style={{
                  position:"relative",
                  background:alreadyClaimed
                    ? `linear-gradient(135deg, ${faction.color}18, ${GM.pageWarm})`
                    : complete
                    ? `linear-gradient(135deg, ${faction.color}22, rgba(0,0,0,0.35))`
                    : isFirstPledge
                    ? `linear-gradient(135deg, ${faction.color}1a, rgba(0,0,0,0.40))`
                    : "rgba(0,0,0,0.45)",
                  border:`1px solid ${alreadyClaimed ? faction.color+"88" : complete ? faction.color+"66" : isFirstPledge ? faction.color+"66" : GM.pageBorder}`,
                  borderRadius:4, padding:"14px 14px 12px",
                  marginBottom:12,
                  boxShadow: complete || isFirstPledge
                    ? `0 0 16px ${faction.color}33, inset 0 0 8px ${faction.color}11`
                    : "inset 0 0 12px rgba(0,0,0,0.3)",
                }}>
                  <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                    {/* Reward card slot */}
                    <div
                      style={{
                        flexShrink:0, width:58, height:80,
                        background:`linear-gradient(145deg, ${GM.pageMid}, ${GM.page})`,
                        border: hasHolo
                          ? `1px solid ${faction.color}`
                          : `1px solid ${showUnlockHint ? faction.color+"66" : GM.pageBorder}`,
                        borderRadius:3, position:"relative", overflow:"hidden",
                        // Breathing per-tier glow via the shared, lifecycle-proof
                        // helper (injects keyframes into <head> once) — replaces
                        // the old green-hardcoded .jesters-holo-slot class so
                        // every faction slot breathes in its own tier's color.
                        ...(hasHolo
                          ? holoGlowStyle(holoTier)
                          : { boxShadow:`inset 0 0 8px rgba(0,0,0,0.7)` }),
                    }}>
                      {rewardImg ? (
                        <img src={rewardImg} alt={rewardName}
                          onError={e => { e.target.style.display = "none"; }}
                          style={{
                            width:"100%", height:"100%", objectFit:"cover",
                            filter: alreadyClaimed ? "none"
                                 : showUnlockHint ? "brightness(0.95)"
                                 : "grayscale(0.7) brightness(0.4) blur(0.5px)",
                            opacity: alreadyClaimed ? 1 : showUnlockHint ? 0.95 : 0.55,
                          }}
                        />
                      ) : (
                        <div style={{
                          position:"absolute", inset:0,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:20, color:GM.inkBrown, opacity:0.3,
                        }}>◆</div>
                      )}

                      {/* ── UNIFIED HOLOGRAPHIC OVERLAY ────────────
                         The premier J'ynxi foil, now rendered by the shared
                         <HoloFoil> so it is byte-for-byte the SAME effect the
                         player sees in the booster shop and on the big pull.
                         (Was hardcoded inline here; the old Session-3 TODO is
                         resolved — this is the reusable component.) */}
                      {hasHolo && (
                        <HoloFoil
                          tier={holoTier}
                          radius={3}
                          corners={true}
                          intensity={showUnlockHint ? 1 : 0.5}
                        />
                      )}

                      {alreadyClaimed && (
                        <div style={{
                          position:"absolute", inset:0,
                          background:`linear-gradient(135deg, transparent 40%, ${faction.color}66)`,
                          display:"flex", alignItems:"flex-end", justifyContent:"flex-end",
                          padding:3,
                        }}>
                          <div style={{
                            fontSize:9, color:faction.color, fontWeight:900,
                            textShadow:"0 0 4px rgba(0,0,0,0.9)",
                          }}>✓</div>
                        </div>
                      )}
                    </div>

                    {/* Reward info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      {/* Reward type pill */}
                      <div style={{
                        fontSize:8, letterSpacing:2,
                        color: faction.color, opacity:0.85, marginBottom:3,
                        fontWeight:900,
                      }}>
                        {rewardCard?.type ? `${rewardCard.type.toUpperCase()}` : "EXCLUSIVE"}
                      </div>
                      {/* Reward name */}
                      <div style={{
                        fontSize:14, fontWeight:900, fontStyle:"italic",
                        color:alreadyClaimed || complete || isFirstPledge ? GM.vellum : GM.vellumDim,
                        fontFamily:GM.font, lineHeight:1.2, marginBottom:4,
                      }}>
                        {rewardName}
                      </div>
                      {/* Unlock condition — OR question marks */}
                      <div style={{
                        fontSize:10, color:GM.vellumFaint, lineHeight:1.4,
                        fontStyle:"italic", fontFamily:GM.font,
                      }}>
                        {alreadyClaimed ? (
                          <span style={{ color:faction.color }}>Earned.</span>
                        ) : complete ? (
                          <span style={{ color:faction.color }}>Ready to claim.</span>
                        ) : isFirstPledge ? (
                          <span style={{ color:faction.color }}>Your first boon. Claim to pledge.</span>
                        ) : showUnlockHint ? (
                          <span>{ch.desc}</span>
                        ) : ch.hint ? (
                          // Cryptic hint for locked rewards (if defined) — mysterious but not opaque.
                          <span style={{
                            color:GM.vellumFaint, letterSpacing:0.5,
                            fontStyle:"italic",
                          }}>{ch.hint}</span>
                        ) : (
                          <span style={{
                            color:GM.inkBrown, letterSpacing:3, fontStyle:"normal",
                          }}>??? ??? ???</span>
                        )}
                      </div>
                    </div>

                    {/* Progress / claim button column */}
                    <div style={{ flexShrink:0, textAlign:"right", minWidth:60 }}>
                      {isFirstPledge ? (
                        <button onClick={() => handleClaim(ch, true)} style={{
                          padding:"8px 12px",
                          background:`linear-gradient(135deg, ${faction.color}33, ${GM.pageMid})`,
                          border:`1px solid ${faction.color}`, borderRadius:3, cursor:"pointer",
                          color:faction.color, fontSize:10, fontFamily:GM.font, fontWeight:900,
                          letterSpacing:1, lineHeight:1.3,
                          boxShadow:`0 0 12px ${faction.color}44`,
                        }}>
                          {claiming === ch.id ? "..." : "◆ CLAIM\n& PLEDGE"}
                        </button>
                      ) : (
                        <>
                          <div style={{
                            fontSize:11, color:complete ? faction.color : GM.vellumFaint,
                            fontFamily:GM.font, fontWeight:900,
                          }}>
                            {isMember ? `${Math.min(prog, ch.goal)}/${ch.goal}` : "—"}
                          </div>
                          {complete && !alreadyClaimed && isMember && (
                            <button onClick={() => handleClaim(ch)} style={{
                              marginTop:6, padding:"6px 12px",
                              background:`linear-gradient(135deg, ${faction.color}33, ${GM.pageMid})`,
                              border:`1px solid ${faction.color}`, borderRadius:3, cursor:"pointer",
                              color:faction.color, fontSize:10, fontFamily:GM.font, fontWeight:900,
                              letterSpacing:1,
                            }}>{claiming === ch.id ? "..." : "Claim"}</button>
                          )}
                          {alreadyClaimed && (
                            <div style={{
                              fontSize:9, color:faction.color, marginTop:6,
                              letterSpacing:2, fontWeight:900,
                            }}>✓ CLAIMED</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Progress bar — only show for members, and only if the hint is revealed */}
                  {isMember && showUnlockHint && !alreadyClaimed && (
                    <div style={{
                      marginTop:10, height:2,
                      background:GM.pageBorder, borderRadius:1, overflow:"hidden",
                    }}>
                      <div style={{
                        height:"100%", borderRadius:1, transition:"width 0.5s",
                        width:`${Math.min(100, (prog/ch.goal)*100)}%`,
                        background:`linear-gradient(90deg, ${faction.color}, ${faction.color}aa)`,
                        boxShadow:`0 0 6px ${faction.color}88`,
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── LEAVE BUTTON ──────────────────────────────────────── */}
        <div style={{ padding:"0 28px 28px" }}>
          <button onClick={onClose} style={{
            width:"100%", padding:"10px",
            background:"transparent",
            border:`1px solid ${GM.inkBrown}55`,
            borderTop:`1px solid ${GM.inkBrown}88`,
            borderRadius:2, color:GM.vellumFaint, cursor:"pointer",
            fontFamily:GM.font, fontSize:11, letterSpacing:3,
          }}>LEAVE</button>
        </div>

        {/* Bottom aura bar — mirror of top */}
        <div style={{
          height:4,
          background:`linear-gradient(90deg, transparent, ${faction.color}, transparent)`,
          opacity:0.4,
        }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function FactionHall({ profile, playerName, uid, onExit }) {
  const companion = useEquippedCompanion(uid);
  const mobile = isMobile();
  const [tile, setTile]           = useState(calcTile);
  const [pos, setPos]             = useState(SPAWN);
  const [facing, setFacing]       = useState({ x: 0, y: 1 });
  const [walkFrame, setWalkFrame] = useState(false);
  // Rest + true-idle come from the engine hook; walkFrame stays local
  // because this screen drives it from its own movement loop.
  const { motion, idleStep } = usePlayerAnimation(pos);
  const [nearby, setNearby]       = useState(null);
  const [dialogue, setDialogue]   = useState(null);
  const [menu, setMenu]           = useState(null);
  // ── THE TREATMENT (C5, 2026-08-18) — the Hall's console ──────
  // Prompts + dialogue now flow through engine/RoomConsole (the
  // Workshop pattern): pill prompt, paged lines, persistent session
  // Record. The hand-rolled ProximityBubble/DialogueBox are gone,
  // along with their JSX <style> keyframes (that trap closes here).
  const [log, setLog] = useState(() => getSessionLog("faction").slice());
  const [playerFaction, setPlayerFaction]         = useState(profile?.faction || null);
  const [isJester, setIsJester]                   = useState(profile?.isJester || false);
  const [prayerCount, setPrayerCount]             = useState(profile?.monolithPrayers || 0);
  const [easterEggUnlocked, setEasterEggUnlocked] = useState(profile?.monolithSecret || false);

  const walkRef = useRef(false);
  const posRef  = useRef(SPAWN);
  const keysRef = useRef({});
  const menuRef = useRef(false);
  const TILE    = tile;

  useEffect(() => {
    const update = () => setTile(calcTile());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => {
    menuRef.current = !!(dialogue || menu);
    if (dialogue || menu) keysRef.current = {};
  }, [dialogue, menu]);

  useEffect(() => {
    keysRef.current = {};
    const down = e => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (dialogue) { setDialogue(null); return; }
        if (menu)     { setMenu(null); return; }
        onExit && onExit();
        return;
      }
      if ((e.key === "q" || e.key === "Q") && dialogue) {
        e.preventDefault(); advanceDialogue(); return;
      }
      if ((e.key === "q" || e.key === "Q") && nearby && !dialogue && !menu) {
        e.preventDefault(); handleSpeak(nearby); return;
      }
      keysRef.current[e.key] = true;
    };
    const up = e => { keysRef.current[e.key] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup",   up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup",   up);
    };
    // NOTE deps deliberately omit handleSpeak/advanceDialogue — they are
    // declared LATER in the component, and a deps-array reference would
    // evaluate before their const initializers run (TDZ crash). `dialogue`
    // in the deps re-binds the listener whenever they'd go stale.
  }, [nearby, dialogue, menu, onExit]);

  // ── Movement — full 4-directional ─────────────────────────
  useEffect(() => {
    let lastMove = 0;
    const loop = setInterval(() => {
      if (menuRef.current) { keysRef.current = {}; return; }
      const now = Date.now();
      if (now - lastMove < 150) return;
      const k = keysRef.current;
      let dx = 0, dy = 0;
      if (k["ArrowUp"]    || k["w"] || k["W"]) dy = -1;
      if (k["ArrowDown"]  || k["s"] || k["S"]) dy =  1;
      if (k["ArrowLeft"]  || k["a"] || k["A"]) dx = -1;
      if (k["ArrowRight"] || k["d"] || k["D"]) dx =  1;
      if (dx === 0 && dy === 0) return;
      lastMove = now;
      setPos(prev => {
        const nx = prev.x + dx, ny = prev.y + dy;
        if (ny >= ROWS) { onExit && onExit(); return prev; }
        if (!isWalkable(nx, ny)) return prev;
        posRef.current = { x: nx, y: ny };
        setFacing({ x: dx, y: dy });
        walkRef.current = !walkRef.current;
        setWalkFrame(walkRef.current);
        setNearby(getNearbyFaction(nx, ny));
        return { x: nx, y: ny };
      });
    }, 50);
    return () => clearInterval(loop);
  }, [onExit]);

  const addLog = useCallback((entry) => {
    setLog(prev => {
      const next = [...prev, entry].slice(-60);
      setSessionLog("faction", next);
      return next;
    });
  }, []);

  // Every dialogue opens through here: stamps idx 0 for the console's
  // paged CONTINUE flow and drops the first line into the Record.
  const openDialogue = useCallback((d) => {
    addLog({ label: d.label, color: d.color, text: d.lines[0] });
    setDialogue({ ...d, idx: 0 });
  }, [addLog]);

  const handleSpeak = useCallback((faction) => {
    if (faction.isMonolith) {
      const newCount = prayerCount + 1;
      setPrayerCount(newCount);
      update(ref(db, `profiles/${uid}`), { monolithPrayers: newCount });
      if (easterEggUnlocked) {
        openDialogue({ lines: faction.easterEgg.secretLines, color: faction.color, label: "The Monolith", faction });
        return;
      }
      // ── PRAY-7 REVEAL ─────────────────────────────────────────
      // At 7+ prayers, the Monolith reveals itself as a faction.
      // We attach `faction` so handleDialogueClose auto-opens the
      // Jesters menu immediately after the dialogue closes — same
      // chain the other factions use after "Speak".
      const revealingAsFaction = newCount >= 7 && !isJester;
      const prayerLines = newCount === 1
        ? ["...", "The pillar hums faintly.", "Something acknowledges you.", "You feel slightly less alone."]
        : newCount < 4
        ? ["...", "Again.", "It remembers you.", "A warmth that shouldn't exist in stone."]
        : newCount < 7
        ? ["...", "You're becoming a regular.", "The Monolith doesn't grant wishes.", "But it does keep score."]
        : newCount === 7
        ? ["...", "Seven.", "The pillar splits in ways your eyes refuse to follow.",
           "A court you never saw announces itself.",
           "— You found J'ynxi's Jesters. —"]
        : isJester
        ? ["...", "Still praying.", "Good.", "Keep score."]
        : ["...", "The court remains open.", "Your pledge awaits — take what is yours."];
      openDialogue({
        lines: prayerLines,
        color: faction.color,
        label: "The Monolith",
        // Attach the Jesters faction object so dialogue-close auto-opens the menu
        faction: revealingAsFaction ? faction : null,
      });
      return;
    }
    const isMember = faction.id === "jesters" ? isJester : playerFaction === faction.id;
    // ⭐ THE RECOGNITION LINE IS SHOWN NOW. This branch tested for
    // `recognitionLines[playerFaction]` and then opened memberLines or
    // the plain greeting instead — so the condition selected the branch
    // and the branch ignored the reason it had been selected. Both
    // recognition lines have been unreachable since they were written:
    // a Jester who also pledged Nullus or Omnis got the ordinary
    // script from a recruiter who had a specific thing to say about it.
    // The line goes FIRST and the normal script follows, so nothing is
    // lost — it is a greeting with an opening remark now.
    const recognition = isJester ? faction.recognitionLines?.[playerFaction] : null;
    if (recognition) {
      openDialogue({
        lines: [recognition, ...(isMember ? faction.memberLines : faction.greeting)],
        color: faction.color, label: faction.rep, faction,
      });
      return;
    }
    openDialogue({ lines: isMember ? faction.memberLines : faction.greeting, color: faction.color, label: faction.rep, faction });
  }, [playerFaction, isJester, prayerCount, easterEggUnlocked, uid, openDialogue]);

  const handleDialogueClose = useCallback(() => {
    const f = dialogue?.faction;
    setDialogue(null);
    if (f) setTimeout(() => setMenu(f), 100);
  }, [dialogue]);

  // Console paging: CONTINUE walks the lines (each into the Record);
  // the final tap closes through handleDialogueClose so the faction
  // menu auto-open chain is preserved. Escape stays a plain dismiss.
  const advanceDialogue = useCallback(() => {
    if (!dialogue) return;
    const next = (dialogue.idx || 0) + 1;
    if (next >= dialogue.lines.length) { handleDialogueClose(); return; }
    addLog({ label: dialogue.label, color: dialogue.color, text: dialogue.lines[next] });
    setDialogue({ ...dialogue, idx: next });
  }, [dialogue, handleDialogueClose, addLog]);

  const handleJoin = async (factionId, opts = {}) => {
    if (!uid) return;
    if (factionId === "jesters") {
      // Note: claim-and-pledge from FactionMenu already writes isJester+member row,
      // so we just flip local state here. Still safe to re-write if called via
      // the regular "Pledge" button flow.
      if (!opts.skipMenuReopen) {
        await update(ref(db, `profiles/${uid}`), { isJester: true });
        await update(ref(db, `factions/jesters/members/${uid}`), { joinedAt: Date.now(), rank: 0, name: playerName });
      }
      setIsJester(true);
      if (!opts.skipMenuReopen) setMenu(FACTIONS.jesters);
      return;
    }
    await update(ref(db, `profiles/${uid}`), { faction: factionId });
    // NEVER set() THIS ROW. It carries every `<challenge>_claimed` stamp,
    // so a full-object write on a re-join hands the player the whole shelf
    // again -- switch away, switch back, claim all three a second time.
    await update(ref(db, `factions/${factionId}/members/${uid}`), { joinedAt: Date.now(), rank: 0, name: playerName });
    setPlayerFaction(factionId);
    setMenu(FACTIONS[factionId]);
  };

  const mapW = COLS * TILE;
  const mapH = ROWS * TILE;

  return (
    <div style={{ position:"fixed", inset:0, background: "transparent",
      overflow:"hidden", fontFamily:GM.font,
      display:"flex", alignItems:"center", justifyContent:"center" }}>
      {/* Letterbox backdrop now paints from RoomOverlay's BackdropUnderlay
          (the overseer) — the room-local copy was redundant double paint.
          The bubbleFloat <style> block left with ProximityBubble (C5). */}

      {/* HUD */}
      <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:50,
        padding:"6px 12px", display:"flex", alignItems:"center", gap:10,
        background:"linear-gradient(180deg, rgba(0,0,1,0.9), transparent)" }}>
        <button onClick={onExit} style={{ background:"transparent",
          border:`1px solid ${GM.inkBrown}66`, color:GM.vellumFaint,
          padding:"3px 10px", borderRadius:2, fontSize:10, cursor:"pointer",
          fontFamily:GM.font }}>← Exit</button>
        <span style={{ fontSize:10, color:GM.inkGold, letterSpacing:3, fontFamily:GM.font }}>◆ THE HALL OF ALLEGIANCE</span>
        {playerFaction && (
          <span style={{ fontSize:9, color:FACTIONS[playerFaction]?.color, letterSpacing:2, marginLeft:8, fontFamily:GM.font }}>
            ◆ {FACTIONS[playerFaction]?.name}
          </span>
        )}
        {isJester && (
          <span style={{ fontSize:9, color:FACTIONS.jesters.color, letterSpacing:2, marginLeft:8, fontFamily:GM.font }}>
            ◆ J'ynxi's Jester
          </span>
        )}

        {/* ── Persistent Jesters access ─────────────────────────
           Appears once the Monolith has revealed itself (Pray 7+ or already Jester).
           Opens the Jesters menu from anywhere in the Hall without walking to the pillar. */}
        {((profile?.monolithPrayers || 0) >= 7 || isJester) && (
          <button
            onClick={() => setMenu(FACTIONS.jesters)}
            style={{
              marginLeft:"auto",
              background:`linear-gradient(135deg, ${FACTIONS.jesters.color}22, transparent)`,
              border:`1px solid ${FACTIONS.jesters.color}88`,
              color:FACTIONS.jesters.color,
              padding:"3px 10px", borderRadius:2,
              fontSize:10, cursor:"pointer",
              fontFamily:GM.font, letterSpacing:1,
              boxShadow:`0 0 10px ${FACTIONS.jesters.color}33`,
            }}>
            ◆ Visit Jesters
          </button>
        )}
      </div>

      {/* MAP */}
      <div style={{ position:"relative", width:mapW, height:mapH, flexShrink:0 }}>

        {/* Background */}
        <img
          key={`${CDN}/p_alliance.webp`}
          src={`${CDN}/p_alliance.webp`}
          alt="Hall of Allegiance"
          onError={e => { e.target.style.display="none"; e.target.parentNode.style.background="linear-gradient(160deg,#0d0a05,#1a1208,#0a0804)"; }}
          style={{ position:"absolute", inset:0, width:"100%", height:"100%",
            objectFit:"contain", imageRendering:"pixelated", zIndex:0, pointerEvents:"none" }}
        />

        {/* Faction rep sprites */}
        {Object.values(FACTIONS).filter(f => !f.isMonolith).map(f => {
          const src     = f.id === "nullus" ? `${CDN}/p_nihil.webp` : `${CDN}/p_omnia.webp`;
          const spriteW = f.id === "nullus" ? TILE * 4 : TILE * 5;
          const spriteH = spriteW;
          return (
            <div key={f.id} style={{
              position:"absolute",
              left: f.x * TILE - spriteW / 2 + TILE / 2,
              top:  f.y * TILE - spriteH + TILE,
              width: spriteW, height: spriteH,
              zIndex:5, pointerEvents:"none",
            }}>
              <img key={src} src={src} alt={f.rep}
                onError={e => e.target.style.display="none"}
                style={{ width:"100%", height:"100%", objectFit:"contain",
                  imageRendering:"pixelated", filter:`drop-shadow(0 0 8px ${f.color}88)` }} />
            </div>
          );
        })}

        {/* Player + Companion — engine */}
        <PlayerEntity
          pos={pos}
          facing={facing}
          gender={profile?.gender || "f"}
          walkFrame={walkFrame}
          motion={motion} idleStep={idleStep}
          tile={TILE}
          companionId={companion.companionId}
          radiantTier={companion.radiantTier}
          showCompanion={true}
        />

        {/* Exit hint */}
        <div style={{ position:"absolute", bottom:TILE * 0.5, left:"50%",
          transform:"translateX(-50%)", fontSize:8, color:GM.vellumFaint,
          letterSpacing:2, fontFamily:GM.font, pointerEvents:"none" }}>
          ▼ EXIT
        </div>
      </div>

      {/* Mobile D-Pad — engine, full 4-directional */}
      {mobile && <MobileDpad keysRef={keysRef} />}

      {/* Room Console — prompt pill, paged dialogue, the session Record
          (The Treatment: same console the Workshop runs; C5 2026-08-18) */}
      <RoomConsole
        mobile={mobile}
        zoneId="faction"
        topOffset={64}
        log={log}
        dialogue={dialogue}
        onAdvance={advanceDialogue}
        nearbyList={(nearby && !menu) ? [{ verb: nearby.isMonolith ? "Pray" : "Speak", label: nearby.rep, color: nearby.color }] : []}
        nearbyIndex={0}
        onActivate={() => nearby && handleSpeak(nearby)}
      />

      {/* Faction menu */}
      {menu && (
        <FactionMenu
          faction={menu}
          playerFaction={playerFaction}
          playerName={playerName}
          uid={uid}
          /* THE LIVE PRAYER COUNT, not the navigation snapshot. `profile`
             is handed over once on entry and never refreshes, so the very
             session in which the player reaches seven prayers would show
             PLEDGE instead of the first boon -- and pledging that way puts
             the boon permanently out of reach. */
          profile={{ ...profile, monolithPrayers: prayerCount }}
          isJester={isJester}
          onJoin={handleJoin}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}